import React from 'react';
import { CheckCircle2, XCircle, ShieldCheck, Loader2 } from 'lucide-react';
import { formatBytes } from '../theme';

/**
 * One row per file actually in flight during a copy — filename, bytes
 * copied/total, percent, and a real state instead of a shared blob.
 *
 * card-ingest.js's copy pipeline has no distinct "verifying" progress event
 * (see _runOffload's runOne: copyFileWithProgress streams onProgress up to
 * bytesCopied === size, then hashFile() runs on both source and dest with no
 * progress callback at all before the file leaves inFlightBytes). So a file
 * that's still present in `progress.activeFiles` with bytesCopied >= size is,
 * by construction, in the checksum-verification step — that's how
 * state === 'verifying' below is derived, not a guess.
 */
const STATE_META = {
  queued: { label: 'Queued', color: 'muted' },
  copying: { label: 'Copying', color: 'accent' },
  verifying: { label: 'Verifying checksum', color: 'warn' },
  done: { label: 'Verified', color: 'ok' },
  error: { label: 'Failed', color: 'err' },
};

export function deriveFileState(file) {
  if (!file) return 'queued';
  if (file.error) return 'error';
  if (file.done) return 'done';
  const size = file.size || 0;
  if (size > 0 && (file.bytesCopied || 0) >= size) return 'verifying';
  return 'copying';
}

export default function ActiveFileProgressRow({ c, file, state }) {
  const resolvedState = state || deriveFileState(file);
  const meta = STATE_META[resolvedState] || STATE_META.copying;
  const pct = file.size ? Math.min(100, Math.round(((file.bytesCopied || 0) / file.size) * 100)) : 0;
  const barColor = resolvedState === 'error' ? c.err : resolvedState === 'done' ? c.ok : resolvedState === 'verifying' ? c.warn : c.accent;

  const Icon = resolvedState === 'done' ? CheckCircle2
    : resolvedState === 'error' ? XCircle
    : resolvedState === 'verifying' ? ShieldCheck
    : Loader2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Icon
          size={12}
          color={c[meta.color]}
          style={resolvedState === 'copying' ? { animation: 'cardIngestSpin 1s linear infinite', flexShrink: 0 } : { flexShrink: 0 }}
        />
        <span
          title={file.relativePath}
          style={{
            fontSize: '11px', color: c.text, fontFamily: 'monospace',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
          }}
        >
          {file.relativePath}
        </span>
        <span style={{ fontSize: '10px', fontWeight: 600, color: c[meta.color], flexShrink: 0, whiteSpace: 'nowrap' }}>
          {meta.label}
        </span>
        {!!file.size && (
          <span style={{ fontSize: '10px', color: c.muted, flexShrink: 0, minWidth: '96px', textAlign: 'right' }}>
            {formatBytes(file.bytesCopied || 0)} / {formatBytes(file.size)}
          </span>
        )}
      </div>
      <div style={{ height: '4px', borderRadius: '2px', background: c.rule, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${resolvedState === 'verifying' ? 100 : pct}%`,
          background: barColor,
          transition: 'width 0.15s linear',
        }} />
      </div>
      {resolvedState === 'error' && file.error && (
        <div style={{ fontSize: '10px', color: c.err }}>{file.error}</div>
      )}
    </div>
  );
}
